import type { RefObject } from "react";

import { useWindowEvent } from "@mantine/hooks";

import { env } from "@/config";
import { useAgentSdk, useAgentStore } from "@/sdk";

interface Props {
    hasActiveVoiceTask: boolean;
    isAuthRedirectNeededRef: RefObject<boolean>;
}

/**
 * Shows the browser's "leave site?" confirmation when a reload or close would drop a voice call: one in
 * progress (simulated ones included), or one that could be routed in mid-reload to an expert Available
 * for voice. Chats rehydrate after a reload, so they don't need it. Skipped while redirecting to the
 * Amazon Connect login.
 */
export const useConfirmBeforeUnload = ({ hasActiveVoiceTask, isAuthRedirectNeededRef }: Props) => {
    const sdk = useAgentSdk();
    const isAvailable = useAgentStore((state) => state.activity === sdk.availableAgentActivity);
    const receivesVoice = useAgentStore((state) => state.supportsChannel("voice"));
    // The chat build doesn't carry voice audio, so a reload there can't drop a call.
    const isAvailableForVoice = env.channel === "voice" && receivesVoice && isAvailable;

    useWindowEvent("beforeunload", (ev) => {
        if (isAuthRedirectNeededRef.current) return;
        if (!hasActiveVoiceTask && !isAvailableForVoice) return;
        ev.preventDefault();
        ev.returnValue = "";
        return "";
    });
};
